import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, User, Globe, Mail, Sparkles, Trash2, Save, Star, BookOpen, Flame, Compass, Share2, KeyRound, Moon, Sun, Snowflake, Heart, Flower2, Gem, ImageDown, Award, Lock, Gift, Copy, Loader2, Camera, Users, UserPlus, UserCheck, Eye, EyeOff, Link2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LearnerProfileCard from "@/components/LearnerProfileCard";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useScreensaverPrefs, setScreensaverPrefs } from "@/lib/screensaverPrefs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CocoonMark } from "@/components/CocoonMark";
import { CoconBadge } from "@/components/CoconBadge";
import { ProfileManager } from "@/components/ProfileManager";
import { CoconHistory } from "@/components/CoconHistory";
import { HouseholdWeather } from "@/components/HouseholdWeather";

const LANGS = [
  { code: "fr", label: "Français" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "nl", label: "Nederlands" },
];

const PLAN_LABELS = { free: "Le Souffle", elan: "L'Élan", cocon: "Le Cocoon" };

const SAVER_OPTIONS = [
  { value: 30000, key: "d30" },
  { value: 60000, key: "d60" },
  { value: 120000, key: "d120" },
  { value: 300000, key: "d300" },
];

const EMBLEMS = [
  { key: "sparkles", Icon: Sparkles },
  { key: "star", Icon: Star },
  { key: "sun", Icon: Sun },
  { key: "moon", Icon: Moon },
  { key: "flower", Icon: Flower2 },
  { key: "diamond", Icon: Gem },
  { key: "snowflake", Icon: Snowflake },
  { key: "heart", Icon: Heart },
];

const BADGES = [
  { key: "star1", Icon: Star, test: (s) => (s.stars || 0) >= 1 },
  { key: "star5", Icon: Sparkles, test: (s) => (s.stars || 0) >= 5 },
  { key: "star15", Icon: Compass, test: (s) => (s.stars || 0) >= 15 },
  { key: "epi1", Icon: BookOpen, test: (s) => (s.epiphanies || 0) >= 1 },
  { key: "epi10", Icon: BookOpen, test: (s) => (s.epiphanies || 0) >= 10 },
  { key: "streak3", Icon: Flame, test: (s) => (s.streak || 0) >= 3 },
  { key: "streak7", Icon: Flame, test: (s) => (s.streak || 0) >= 7 },
  { key: "days30", Icon: Award, test: (s) => (s.active_days || 0) >= 30 },
];


export default function Profil() {
  const { t, i18n } = useTranslation();
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [refCode, setRefCode] = useState(null);
  const [refData, setRefData] = useState({ count: 0, reward_days_left: 0 });
  const [motto, setMotto] = useState(user?.motto || "");
  const [savingMotto, setSavingMotto] = useState(false);
  const [passportLoading, setPassportLoading] = useState(false);
  const [explorer, setExplorer] = useState(null);
  const [publicBusy, setPublicBusy] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followBusy, setFollowBusy] = useState("");
  const [photoBusy, setPhotoBusy] = useState(false);
  const [cb, setCb] = useState(Date.now());
  const fileRef = useRef(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const avatarUrl = user?.avatar_photo ? `${BACKEND_URL}/api/avatar/${user.id}?v=${cb}` : null;

  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const currentLang = (i18n.language || "fr").slice(0, 2);

  const saverPrefs = useScreensaverPrefs();
  const saverSelected = saverPrefs.enabled ? saverPrefs.delayMs : "off";
  const chooseSaver = (val) => {
    if (val === "off") setScreensaverPrefs({ enabled: false });
    else setScreensaverPrefs({ enabled: true, delayMs: val });
    toast.success(t("screensaver.saved"));
  };

  useEffect(() => {
    api.get("/me/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/referral/me").then(({ data }) => {
      setRefCode(data.code || null);
      setRefData({ count: data.count || 0, reward_days_left: data.reward_days_left || 0 });
    }).catch(() => {});
    api.get("/me/explorer").then(({ data }) => setExplorer(data)).catch(() => {});
    api.get("/explorers/similar").then(({ data }) => setSimilar(data.explorers || [])).catch(() => {});
    api.get("/me/following").then(({ data }) => setFollowing(data.following || [])).catch(() => {});
  }, []);

  useEffect(() => { setMotto(user?.motto || ""); }, [user?.motto]);

  const emblemKey = user?.avatar || "sparkles";
  const EmblemIcon = (EMBLEMS.find((e) => e.key === emblemKey) || EMBLEMS[0]).Icon;
  const emblemIconFor = (key) => (EMBLEMS.find((e) => e.key === key) || EMBLEMS[0]).Icon;
  const isCocon = user?.role === "admin" || user?.plan === "cocon";

  const chooseEmblem = async (key) => {
    try {
      await api.put("/preferences", { avatar: key });
      await refresh();
      toast.success(t("atelier.profile.emblemSaved"));
    } catch { toast.error(t("atelier.profile.error")); }
  };

  const saveMotto = async () => {
    setSavingMotto(true);
    try {
      await api.put("/preferences", { motto: motto.trim() });
      await refresh();
      toast.success(t("atelier.profile.mottoSaved"));
    } catch { toast.error(t("atelier.profile.error")); }
    finally { setSavingMotto(false); }
  };

  const sharePassport = async () => {
    if (passportLoading) return;
    setPassportLoading(true);
    try {
      const { data: blob } = await api.get("/me/passport-card", { params: { lang: currentLang }, responseType: "blob" });
      const file = new File([blob], "cocoon-passeport.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try { await navigator.share({ files: [file], text: t("atelier.profile.passportTitle") }); } catch { /* cancelled */ }
      } else {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = file.name; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(u), 60000);
        toast.success(t("atelier.profile.passportReady"));
      }
    } catch { toast.error(t("atelier.profile.passportError")); }
    finally { setPassportLoading(false); }
  };

  const sharePublicLink = async () => {
    if (!explorer?.handle) return;
    const url = `${window.location.origin}/explorateur/${explorer.handle}`;
    const text = t("atelier.profile.publicShareText");
    if (navigator.share) {
      try { await navigator.share({ title: "COCOON", text, url }); return; } catch { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success(t("atelier.profile.publicLinkCopied")); }
    catch { toast.error(t("atelier.profile.error")); }
  };

  const [ritualBusy, setRitualBusy] = useState(false);
  const RTXT = {
    fr: { on: "Rituel du matin activé", off: "Activer le rituel du matin", test: "M'envoyer un test", onMsg: "Rituel du matin activé — à demain matin.", offMsg: "Rituel du matin désactivé.", sent: "E-mail de test envoyé !" },
    en: { on: "Morning ritual on", off: "Enable morning ritual", test: "Send me a test", onMsg: "Morning ritual enabled — see you tomorrow morning.", offMsg: "Morning ritual disabled.", sent: "Test email sent!" },
    es: { on: "Ritual matinal activado", off: "Activar el ritual matinal", test: "Enviarme una prueba", onMsg: "Ritual matinal activado — hasta mañana.", offMsg: "Ritual matinal desactivado.", sent: "¡Correo de prueba enviado!" },
    nl: { on: "Ochtendritueel aan", off: "Ochtendritueel inschakelen", test: "Stuur me een test", onMsg: "Ochtendritueel ingeschakeld — tot morgenochtend.", offMsg: "Ochtendritueel uitgeschakeld.", sent: "Testmail verzonden!" },
  };
  const rt = RTXT[(i18n.language || "fr").slice(0, 2)] || RTXT.fr;

  const toggleRitual = async () => {
    if (ritualBusy) return;
    setRitualBusy(true);
    const next = !user?.morning_ritual;
    try {
      await api.put("/preferences", { morning_ritual: next });
      await refresh();
      toast.success(next ? rt.onMsg : rt.offMsg);
    } catch { toast.error(t("atelier.profile.error")); }
    finally { setRitualBusy(false); }
  };

  const sendRitualTest = async () => {
    if (ritualBusy) return;
    setRitualBusy(true);
    try {
      await api.post(`/ritual/test?lang=${(i18n.language || "fr").slice(0, 2)}`);
      toast.success(rt.sent);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.profile.error")); }
    finally { setRitualBusy(false); }
  };

  const togglePublic = async () => {
    if (!explorer) return;
    setPublicBusy(true);
    const next = !explorer.public_profile;
    try {
      await api.put("/preferences", { public_profile: next });
      setExplorer((e) => ({ ...e, public_profile: next }));
      toast.success(next ? t("atelier.profile.publicOn") : t("atelier.profile.publicOff"));
    } catch { toast.error(t("atelier.profile.error")); }
    finally { setPublicBusy(false); }
  };

  const toggleFollow = async (ex, listSetter) => {
    if (followBusy) return;
    setFollowBusy(ex.handle);
    try {
      if (ex.is_following) {
        await api.delete(`/explorer/${ex.handle}/follow`);
      } else {
        await api.post(`/explorer/${ex.handle}/follow`);
      }
      const nowFollowing = !ex.is_following;
      setSimilar((arr) => arr.map((x) => x.handle === ex.handle ? { ...x, is_following: nowFollowing } : x));
      // refresh following list
      const { data } = await api.get("/me/following");
      setFollowing(data.following || []);
    } catch { toast.error(t("atelier.profile.error")); }
    finally { setFollowBusy(""); }
  };

  const copyReferral = async () => {
    const url = refCode ? `${window.location.origin}/entrer?ref=${refCode}` : window.location.origin;
    try { await navigator.clipboard.writeText(url); toast.success(t("atelier.profile.referralCopiedMsg")); } catch { /* ignore */ }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/me/avatar-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setCb(Date.now());
      toast.success(t("atelier.profile.photoSaved"));
    } catch { toast.error(t("atelier.profile.photoError")); }
    finally { setPhotoBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removePhoto = async () => {
    setPhotoBusy(true);
    try {
      await api.delete("/me/avatar-photo");
      await refresh();
      setCb(Date.now());
      toast.success(t("atelier.profile.photoRemoved"));
    } catch { toast.error(t("atelier.profile.photoError")); }
    finally { setPhotoBusy(false); }
  };

  const shareJourney = async () => {
    if (!summary) return;
    const text = t("atelier.profile.journeyShareText", {
      stars: summary.stars, epiphanies: summary.epiphanies, days: summary.active_days,
    });
    const url = refCode
      ? `${window.location.origin}/entrer?ref=${refCode}`
      : window.location.origin;
    if (navigator.share) {
      try { await navigator.share({ title: "COCOON", text, url }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success(t("atelier.profile.journeyShared"));
    } catch {
      toast.error(t("atelier.profile.error"));
    }
  };

  const saveName = async () => {
    if (!name.trim() || name.trim() === user?.name) return;
    setSavingName(true);
    try {
      await api.put("/preferences", { name: name.trim() });
      await refresh();
      toast.success(t("atelier.profile.nameSaved"));
    } catch {
      toast.error(t("atelier.profile.error"));
    } finally {
      setSavingName(false);
    }
  };

  const changeLang = async (code) => {
    if (code === currentLang) return;
    i18n.changeLanguage(code);
    try {
      await api.put("/preferences", { lang: code });
      toast.success(t("atelier.profile.langSaved"));
    } catch {
      /* language still applied client-side */
    }
  };

  const changePassword = async () => {
    if (pwNew.length < 6) { toast.error(t("atelier.profile.pwTooShort")); return; }
    if (pwNew !== pwConfirm) { toast.error(t("atelier.profile.pwMismatch")); return; }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success(t("atelier.profile.pwSuccess"));
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.profile.pwError"));
    } finally {
      setSavingPw(false);
    }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/account");
      toast.success(t("atelier.profile.deleted"));
      await logout();
      navigate("/");
    } catch {
      toast.error(t("atelier.profile.error"));
      setDeleting(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background text-foreground starfield overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-8%", left: "50%", transform: "translateX(-50%)", width: "860px", height: "620px" }} />
      <div className="amber-glow" aria-hidden style={{ top: "64%", left: "-14%", width: "600px", height: "600px", opacity: 0.55 }} />
      <header className="glass sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="profile-back-btn"
            onClick={() => navigate("/atelier")}
            className="flex items-center gap-2 text-sm text-foreground/60 hover:text-primary transition-colors"
          >
            <ArrowLeft size={18} />
            {t("atelier.profile.back")}
          </button>
          <span className="font-display text-2xl tracking-tight"><CocoonMark withMind /></span>
        </div>
      </header>

      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-3xl mx-auto px-6 py-14"
        data-testid="profile-page"
      >
        <div className="text-center mb-12">
          <h1 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight mb-4">
            {t("atelier.profile.title")}
          </h1>
          <p className="text-foreground/55 leading-relaxed">{t("atelier.profile.subtitle")}</p>
        </div>

        {/* Identity: emblem + motto + member since + passport */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
          className="glass rounded-2xl p-6 mb-5 frame-pulse" data-testid="profile-identity"
        >
          <div className="flex items-center gap-4 mb-6">
            <span className={`flex-shrink-0 rounded-full ${isCocon ? "member-cocon-ring" : ""}`}>
            <button type="button" onClick={() => fileRef.current?.click()} disabled={photoBusy}
              className="relative w-16 h-16 rounded-full flex items-center justify-center bg-primary/10 border border-primary/30 flex-shrink-0 overflow-hidden group"
              style={{ boxShadow: "0 0 24px hsl(32 90% 55% / 0.22)" }} data-testid="profile-emblem-display" title={t("atelier.profile.photoUpload")}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" className="w-full h-full object-cover" data-testid="profile-photo" />
                : <EmblemIcon className="text-primary" size={30} strokeWidth={1.6} />}
              <span className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                {photoBusy ? <Loader2 size={18} className="animate-spin text-white" /> : <Camera size={18} className="text-white" />}
              </span>
            </button>
            </span>
            <div className="min-w-0">
              <p className="font-display text-2xl text-foreground/95 truncate flex items-center gap-2">{user?.name || "—"}{isCocon && <CoconBadge />}</p>
              {summary?.member_since && (
                <p className="text-xs text-foreground/50 mt-0.5" data-testid="profile-member-since">
                  {t("atelier.profile.memberSince")} {summary.member_since}
                  {typeof summary.days_since === "number" && <> · <span className="text-primary/70">{t("atelier.profile.wonderDays", { count: summary.days_since })}</span></>}
                </p>
              )}
              <div className="flex items-center gap-3 mt-1.5">
                <button onClick={() => fileRef.current?.click()} disabled={photoBusy} data-testid="profile-photo-upload"
                  className="text-xs text-primary/80 hover:text-primary inline-flex items-center gap-1.5">
                  <Camera size={13} /> {t("atelier.profile.photoUpload")}
                </button>
                {user?.avatar_photo && (
                  <button onClick={removePhoto} disabled={photoBusy} data-testid="profile-photo-remove"
                    className="text-xs text-foreground/40 hover:text-red-400 inline-flex items-center gap-1.5">
                    <Trash2 size={12} /> {t("atelier.profile.photoRemove")}
                  </button>
                )}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-input" />
          </div>

          {/* emblem picker */}
          <p className="text-sm text-primary/80 mb-3">{t("atelier.profile.emblemLabel")}</p>
          <div className="flex flex-wrap gap-2.5 mb-6" data-testid="profile-emblem-picker">
            {EMBLEMS.map(({ key, Icon }) => (
              <button key={key} onClick={() => chooseEmblem(key)} data-testid={`emblem-${key}`}
                className={`w-11 h-11 rounded-full flex items-center justify-center border transition-all ${emblemKey === key ? "bg-primary/20 border-primary text-primary scale-105" : "bg-white/[0.03] border-white/10 text-foreground/50 hover:text-primary hover:border-primary/40"}`}>
                <Icon size={19} strokeWidth={1.7} />
              </button>
            ))}
          </div>

          {/* motto */}
          <label className="block text-sm text-primary/80 mb-2">{t("atelier.profile.mottoLabel")}</label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="profile-motto-input" value={motto} maxLength={90}
              onChange={(e) => setMotto(e.target.value)} placeholder={t("atelier.profile.mottoPlaceholder")}
              className="flex-1 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-foreground/90 focus:outline-none focus:border-primary/50"
            />
            <button onClick={saveMotto} disabled={savingMotto} data-testid="profile-motto-save"
              className="btn-frost-orange px-5 py-3 rounded-xl font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {savingMotto ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {t("atelier.profile.mottoSave")}
            </button>
          </div>

          {/* passport */}
          <div className="mt-6 pt-5 border-t border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div>
                <p className="font-display text-lg">{t("atelier.profile.passportTitle")}</p>
                <p className="text-sm text-foreground/50">{t("atelier.profile.passportDesc")}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={sharePublicLink} disabled={!explorer?.public_profile} data-testid="profile-public-share-btn"
                  className="btn-frost-orange px-5 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                  <Link2 size={16} /> {t("atelier.profile.publicShareBtn")}
                </button>
                <button onClick={sharePassport} disabled={passportLoading} data-testid="profile-passport-btn" title={t("atelier.profile.passportBtn")}
                  className="px-4 py-3 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {passportLoading ? <Loader2 size={16} className="animate-spin" /> : <ImageDown size={16} />}
                </button>
              </div>
            </div>
            {/* public toggle + sparks received */}
            <div className="mt-4 flex flex-wrap items-center gap-4">
              <button onClick={togglePublic} disabled={publicBusy} data-testid="profile-public-toggle"
                className={`inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border transition-colors ${explorer?.public_profile ? "border-primary/40 text-primary bg-primary/10" : "border-white/10 text-foreground/50 hover:text-foreground/80"}`}>
                {explorer?.public_profile ? <Eye size={14} /> : <EyeOff size={14} />}
                {explorer?.public_profile ? t("atelier.profile.publicIsOn") : t("atelier.profile.publicIsOff")}
              </button>
              {explorer && explorer.sparks > 0 && (
                <span className="inline-flex items-center gap-1.5 text-sm text-primary/80" data-testid="profile-sparks-received">
                  <Sparkles size={14} /> {t("atelier.profile.sparksReceived", { count: explorer.sparks })}
                </span>
              )}
              {user?.ritual_enabled && (
                <>
                  <button onClick={toggleRitual} disabled={ritualBusy} data-testid="profile-ritual-toggle"
                    className={`btn-glass-soft inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full ${user?.morning_ritual ? "text-primary" : "text-foreground/60 hover:text-foreground/90"}`}>
                    {ritualBusy ? <Loader2 size={14} className="animate-spin" /> : <Sun size={14} />}
                    {user?.morning_ritual ? rt.on : rt.off}
                  </button>
                  {user?.morning_ritual && (
                    <button onClick={sendRitualTest} disabled={ritualBusy} data-testid="profile-ritual-test"
                      className="btn-glass-soft inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full text-foreground/60 hover:text-primary disabled:opacity-60">
                      <Mail size={14} /> {rt.test}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Family / Explorers management */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="glass rounded-2xl p-6 mb-5"
          data-testid="profile-family"
        >
          <ProfileManager />
        </motion.div>

        {/* Histoire de ton Cocon (humeur au fil du temps) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.08 }}
          className="glass rounded-2xl p-6 mb-5"
          data-testid="profile-cocon-history"
        >
          <CoconHistory />
        </motion.div>

        {/* Météo du Foyer (vue parent, tous les explorateurs) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="glass rounded-2xl p-6 mb-5"
          data-testid="profile-household-weather"
        >
          <HouseholdWeather />
        </motion.div>

        {/* Journey summary */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="stats-wow rounded-2xl p-6 mb-5"
            data-testid="profile-journey"
          >
            <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
              <h2 className="flex items-center gap-2 font-display text-lg text-foreground/90">
                <Compass size={18} className="text-primary" /> {t("atelier.profile.journeyTitle")}
              </h2>
              <button
                data-testid="profile-share-journey"
                onClick={shareJourney}
                className="flex items-center gap-2 text-sm px-4 py-2 rounded-full btn-frost-orange"
              >
                <Share2 size={15} /> {t("atelier.profile.journeyShare")}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: Star, value: summary.stars, label: t("atelier.profile.journeyStars"), test: "stars" },
                { icon: BookOpen, value: summary.epiphanies, label: t("atelier.profile.journeyEpiphanies"), test: "epiphanies" },
                { icon: Flame, value: summary.streak, label: t("atelier.profile.journeyStreak"), test: "streak" },
                { icon: Sparkles, value: summary.active_days, label: t("atelier.profile.journeyDays"), test: "days" },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.test} className="text-center" data-testid={`profile-stat-${s.test}`}>
                    <Icon size={18} className="text-primary/70 mx-auto mb-2" />
                    <motion.p
                      initial={{ opacity: 0, scale: 0.7 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: 0.2 + i * 0.08 }}
                      className="font-display text-3xl text-gradient-orange tabular-nums"
                    >
                      {s.value}
                    </motion.p>
                    <p className="text-xs text-foreground/50 mt-1">{s.label}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Badges / éclats */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
            className="glass rounded-2xl p-6 mb-5" data-testid="profile-badges"
          >
            <h2 className="flex items-center gap-2 font-display text-lg text-foreground/90 mb-1">
              <Award size={18} className="text-primary" /> {t("atelier.profile.badgesTitle")}
            </h2>
            <p className="text-sm text-foreground/50 mb-5">{t("atelier.profile.badgesDesc")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {BADGES.map(({ key, Icon, test }) => {
                const unlocked = test(summary);
                return (
                  <div key={key} data-testid={`badge-${key}`} className={`text-center rounded-2xl p-4 border transition-all ${unlocked ? "bg-primary/[0.07] border-primary/25" : "bg-white/[0.02] border-white/5 opacity-55"}`}>
                    <span className={`w-11 h-11 rounded-full mx-auto mb-2 flex items-center justify-center ${unlocked ? "bg-primary/15 text-primary" : "bg-white/[0.04] text-foreground/40"}`}>
                      {unlocked ? <Icon size={20} strokeWidth={1.7} /> : <Lock size={16} />}
                    </span>
                    <p className={`text-sm font-medium leading-tight ${unlocked ? "text-foreground/90" : "text-foreground/50"}`}>{t(`atelier.profile.badges.${key}.name`)}</p>
                    <p className="text-[11px] text-foreground/40 mt-1">{unlocked ? t(`atelier.profile.badges.${key}.hint`) : t("atelier.profile.badgeLocked")}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Referral / parrainage */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.16 }}
          className="glass rounded-2xl p-6 mb-5" data-testid="profile-referral"
        >
          <h2 className="flex items-center gap-2 font-display text-lg text-foreground/90 mb-1">
            <Gift size={18} className="text-primary" /> {t("atelier.profile.referralProfileTitle")}
          </h2>
          <p className="text-sm text-foreground/50 mb-5">{t("atelier.profile.referralProfileDesc")}</p>
          <div className="flex items-center gap-4 mb-5">
            <div className="text-center px-5 py-3 rounded-2xl bg-primary/[0.07] border border-primary/20">
              <p className="font-display text-3xl text-gradient-orange tabular-nums" data-testid="referral-count">{refData.count}</p>
              <p className="text-xs text-foreground/50 mt-0.5">{t("atelier.profile.referralFriends", { count: refData.count })}</p>
            </div>
            {refData.reward_days_left > 0 && (
              <p className="text-sm text-primary/80" data-testid="referral-reward">✦ {t("atelier.profile.referralReward", { count: refData.reward_days_left })}</p>
            )}
          </div>
          <button onClick={copyReferral} data-testid="profile-referral-copy"
            className="btn-frost-orange px-5 py-3 rounded-full font-semibold inline-flex items-center justify-center gap-2">
            <Copy size={16} /> {t("atelier.profile.referralCopyBtn")}
          </button>
        </motion.div>

        {/* Explorateurs qui te ressemblent (D) */}
        {similar.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.17 }}
            className="glass rounded-2xl p-6 mb-5" data-testid="profile-similar"
          >
            <h2 className="flex items-center gap-2 font-display text-lg text-foreground/90 mb-1">
              <Users size={18} className="text-primary" /> {t("atelier.profile.similarTitle")}
            </h2>
            <p className="text-sm text-foreground/50 mb-5">{t("atelier.profile.similarDesc")}</p>
            <div className="space-y-3">
              {similar.map((ex) => {
                const Icon = emblemIconFor(ex.emblem);
                return (
                  <div key={ex.handle} data-testid={`similar-${ex.handle}`} className="flex items-center gap-3 rounded-xl border border-white/8 bg-white/[0.02] p-3">
                    <span className={ex.cocon ? "member-cocon-ring rounded-full flex-shrink-0" : "flex-shrink-0"} title={ex.cocon ? "Membre Le Cocoon" : undefined}>
                    <button onClick={() => navigate(`/explorateur/${ex.handle}`)} className="w-11 h-11 rounded-full overflow-hidden flex items-center justify-center bg-primary/10 border border-primary/25 flex-shrink-0">
                      {ex.avatar ? <img src={`${BACKEND_URL}/api/avatar/${ex.id}`} alt="" className="w-full h-full object-cover" /> : <Icon size={20} className="text-primary" />}
                    </button>
                    </span>
                    <button onClick={() => navigate(`/explorateur/${ex.handle}`)} className="min-w-0 flex-1 text-left">
                      <p className="font-display text-foreground/90 truncate flex items-center gap-1.5">{ex.name}{ex.cocon && <CoconBadge />}</p>
                      <p className="text-[11px] text-foreground/45 truncate">{t("atelier.profile.sharedDomains", { count: ex.overlap })} · {ex.shared.join(", ")}</p>
                    </button>
                    <button onClick={() => toggleFollow(ex)} disabled={followBusy === ex.handle} data-testid={`follow-${ex.handle}`}
                      className={`flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border transition-colors ${ex.is_following ? "border-primary/40 text-primary bg-primary/10" : "border-white/12 text-foreground/60 hover:text-primary hover:border-primary/40"}`}>
                      {followBusy === ex.handle ? <Loader2 size={13} className="animate-spin" /> : ex.is_following ? <UserCheck size={13} /> : <UserPlus size={13} />}
                      {ex.is_following ? t("atelier.profile.followingBtn") : t("atelier.profile.followBtn")}
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Explorateurs que je suis */}
        {following.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.18 }}
            className="glass rounded-2xl p-6 mb-5" data-testid="profile-following"
          >
            <h2 className="flex items-center gap-2 font-display text-lg text-foreground/90 mb-1">
              <UserCheck size={18} className="text-primary" /> {t("atelier.profile.followingTitle")}
            </h2>
            <p className="text-sm text-foreground/50 mb-5">{t("atelier.profile.followingDesc")}</p>
            <div className="flex flex-wrap gap-3">
              {following.map((ex) => {
                const Icon = emblemIconFor(ex.emblem);
                return (
                  <button key={ex.handle} onClick={() => navigate(`/explorateur/${ex.handle}`)} data-testid={`following-${ex.handle}`}
                    className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.02] pl-1.5 pr-4 py-1.5 hover:border-primary/40 transition-colors">
                    <span className={ex.cocon ? "member-cocon-ring rounded-full flex-shrink-0" : "w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-primary/10 border border-primary/25 flex-shrink-0"} title={ex.cocon ? "Membre Le Cocoon" : undefined}>
                      {ex.cocon ? (
                        <span className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-primary/10 flex-shrink-0">
                          {ex.avatar ? <img src={`${BACKEND_URL}/api/avatar/${ex.id}`} alt="" className="w-full h-full object-cover" /> : <Icon size={15} className="text-primary" />}
                        </span>
                      ) : (
                        ex.avatar ? <img src={`${BACKEND_URL}/api/avatar/${ex.id}`} alt="" className="w-full h-full object-cover" /> : <Icon size={15} className="text-primary" />
                      )}
                    </span>
                    <span className="text-sm text-foreground/85 inline-flex items-center gap-1.5">{ex.name}{ex.cocon && <CoconBadge />}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Learner profile (subscribers) */}
        <LearnerProfileCard />

        {/* Name */}
        <div className="glass rounded-2xl p-6 mb-5 frame-pulse">
          <label className="flex items-center gap-2 text-sm text-primary/80 mb-3">
            <User size={16} /> {t("atelier.profile.nameLabel")}
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              data-testid="profile-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("atelier.profile.namePlaceholder")}
              className="bg-background/40 border-white/10"
            />
            <Button
              data-testid="profile-name-save"
              onClick={saveName}
              disabled={savingName || !name.trim() || name.trim() === user?.name}
              className="btn-frost-orange whitespace-nowrap"
            >
              <Save size={16} className="mr-2" />
              {savingName ? t("atelier.profile.saving") : t("atelier.profile.save")}
            </Button>
          </div>
        </div>

        {/* Language */}
        <div className="glass rounded-2xl p-6 mb-5 frame-pulse">
          <label className="flex items-center gap-2 text-sm text-primary/80 mb-1">
            <Globe size={16} /> {t("atelier.profile.langLabel")}
          </label>
          <p className="text-xs text-foreground/45 mb-4">{t("atelier.profile.langHint")}</p>
          <div className="flex flex-wrap gap-2">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`profile-lang-${l.code}`}
                onClick={() => changeLang(l.code)}
                className={`px-4 py-2 rounded-full text-sm transition-colors border ${
                  currentLang === l.code
                    ? "bg-primary/15 text-primary border-primary/40"
                    : "text-foreground/55 border-white/10 hover:text-foreground/80 hover:border-white/20"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Screensaver / idle mode */}
        <div className="glass rounded-2xl p-6 mb-5 frame-pulse" data-testid="profile-screensaver-card">
          <label className="flex items-center gap-2 text-sm text-primary/80 mb-1">
            <Moon size={16} /> {t("screensaver.label")}
          </label>
          <p className="text-xs text-foreground/45 mb-4">{t("screensaver.hint")}</p>
          <div className="flex flex-wrap gap-2">
            {SAVER_OPTIONS.map((o) => (
              <button
                key={o.value}
                data-testid={`profile-screensaver-${o.key}`}
                onClick={() => chooseSaver(o.value)}
                className={`px-4 py-2 rounded-full text-sm transition-colors border ${
                  saverSelected === o.value
                    ? "bg-primary/15 text-primary border-primary/40"
                    : "text-foreground/55 border-white/10 hover:text-foreground/80 hover:border-white/20"
                }`}
              >
                {t(`screensaver.${o.key}`)}
              </button>
            ))}
            <button
              data-testid="profile-screensaver-off"
              onClick={() => chooseSaver("off")}
              className={`px-4 py-2 rounded-full text-sm transition-colors border ${
                saverSelected === "off"
                  ? "bg-primary/15 text-primary border-primary/40"
                  : "text-foreground/55 border-white/10 hover:text-foreground/80 hover:border-white/20"
              }`}
            >
              {t("screensaver.off")}
            </button>
          </div>
        </div>

        {/* Password */}
        <div className="glass rounded-2xl p-6 mb-5 frame-pulse" data-testid="profile-password-card">
          <label className="flex items-center gap-2 text-sm text-primary/80 mb-4">
            <KeyRound size={16} /> {t("atelier.profile.pwTitle")}
          </label>
          <div className="space-y-3">
            <Input
              data-testid="profile-pw-current"
              type="password"
              value={pwCurrent}
              onChange={(e) => setPwCurrent(e.target.value)}
              placeholder={t("atelier.profile.pwCurrent")}
              autoComplete="current-password"
              className="bg-background/40 border-white/10"
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <Input
                data-testid="profile-pw-new"
                type="password"
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                placeholder={t("atelier.profile.pwNew")}
                autoComplete="new-password"
                className="bg-background/40 border-white/10"
              />
              <Input
                data-testid="profile-pw-confirm"
                type="password"
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                placeholder={t("atelier.profile.pwConfirm")}
                autoComplete="new-password"
                className="bg-background/40 border-white/10"
              />
            </div>
            <Button
              data-testid="profile-pw-save"
              onClick={changePassword}
              disabled={savingPw || !pwCurrent || !pwNew || !pwConfirm}
              className="btn-frost-orange whitespace-nowrap"
            >
              <KeyRound size={16} className="mr-2" />
              {savingPw ? t("atelier.profile.pwSaving") : t("atelier.profile.pwSave")}
            </Button>
          </div>
        </div>

        {/* Account info (read-only) */}
        <div className="glass rounded-2xl p-6 mb-5 frame-pulse grid sm:grid-cols-2 gap-5">
          <div>
            <p className="flex items-center gap-2 text-sm text-primary/80 mb-2">
              <Mail size={16} /> {t("atelier.profile.emailLabel")}
            </p>
            <p className="text-foreground/70 break-all" data-testid="profile-email">{user?.email}</p>
          </div>
          <div>
            <p className="flex items-center gap-2 text-sm text-primary/80 mb-2">
              <Sparkles size={16} /> {t("atelier.profile.planLabel")}
            </p>
            <p className="text-foreground/70" data-testid="profile-plan">{PLAN_LABELS[user?.plan] || "Le Souffle"}</p>
            <button
              data-testid="profile-manage-sub"
              onClick={() => navigate("/abonnement")}
              className="mt-3 inline-flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <Sparkles size={14} /> {t("atelier.profile.manageSub")}
            </button>
          </div>
        </div>

        {/* Danger zone */}
        <div className="rounded-2xl p-6 border border-red-500/25 bg-red-500/[0.04]" data-testid="profile-danger-zone">
          <h2 className="flex items-center gap-2 font-display text-lg text-red-300/90 mb-2">
            <Trash2 size={18} /> {t("atelier.profile.dangerTitle")}
          </h2>
          <p className="text-sm text-foreground/55 leading-relaxed mb-5">{t("atelier.profile.dangerDesc")}</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                data-testid="profile-delete-btn"
                variant="destructive"
                className="bg-red-600/80 hover:bg-red-600 text-white"
              >
                <Trash2 size={16} className="mr-2" />
                {t("atelier.profile.deleteBtn")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="glass border-white/10" data-testid="profile-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-foreground">{t("atelier.profile.deleteConfirmTitle")}</AlertDialogTitle>
                <AlertDialogDescription className="text-foreground/60">
                  {t("atelier.profile.deleteConfirmDesc")}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="profile-delete-cancel">{t("atelier.profile.deleteCancel")}</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="profile-delete-confirm"
                  onClick={deleteAccount}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? t("atelier.profile.deleting") : t("atelier.profile.deleteConfirm")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </motion.section>
    </div>
  );
}
